import type { Stroke, StrokePart } from './types'

/**
 * 落書きの幾何計算。ここには Leaflet も React も持ち込まない
 * （純粋な関数だけにして、描画側と消しゴム側の両方から使う）。
 */

/** 座標は小数4桁で持つ。仮想空間(高さ1000)で 0.1px 相当あり精度は足りる */
export const round4 = (n: number) => Math.round(n * 10000) / 10000

/**
 * 点列を SVG のパス文字列にする。
 * 点をそのまま L で繋ぐと角ばるので、中点を通る二次ベジェで滑らかにする。
 *
 *   M p0  Q p1 mid(p1,p2)  Q p2 mid(p2,p3)  …  L pn
 *
 * @param pts 平坦配列 [x0,y0,x1,y1,...]（0..1）
 * @param w   仮想空間の幅（高さは 1000 固定）
 * @param h   仮想空間の高さ
 */
export function toPathData(pts: number[], w: number, h: number): string {
  const n = pts.length >> 1
  if (n === 0) return ''
  const px = (i: number) => round4(pts[i * 2] * w)
  const py = (i: number) => round4(pts[i * 2 + 1] * h)

  // 1点だけ（タップ）は長さ0の線。stroke-linecap="round" で丸い点になる
  if (n === 1) return `M ${px(0)} ${py(0)} L ${px(0)} ${py(0)}`
  if (n === 2) return `M ${px(0)} ${py(0)} L ${px(1)} ${py(1)}`

  let d = `M ${px(0)} ${py(0)}`
  for (let i = 1; i < n - 1; i++) {
    const mx = round4((px(i) + px(i + 1)) / 2)
    const my = round4((py(i) + py(i + 1)) / 2)
    d += ` Q ${px(i)} ${py(i)} ${mx} ${my}`
  }
  d += ` L ${px(n - 1)} ${py(n - 1)}`
  return d
}

/**
 * 点 c と線分 ab の距離の2乗。
 * 消しゴムの判定を「点」ではなく「線分」で行うために要る
 * （点だけで見ると、点のまばらな直線を消しゴムがすり抜ける）。
 */
function distSqToSegment(cx: number, cy: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  let t = 0
  if (len2 > 0) {
    t = ((cx - ax) * dx + (cy - ay) * dy) / len2
    t = t < 0 ? 0 : t > 1 ? 1 : t
  }
  const qx = ax + t * dx - cx
  const qy = ay + t * dy - cy
  return qx * qx + qy * qy
}

/**
 * 消しゴム円が線に触れているか。
 * 座標系の縦横比が 1 でないので、判定は仮想空間（x を w 倍、y を h 倍）で行う。
 */
export function hitsStroke(stroke: Stroke, cx: number, cy: number, r: number, w: number, h: number): boolean {
  const pts = stroke.pts
  const n = pts.length >> 1
  if (n === 0) return false
  // 線幅のぶんだけ当たりを広げる（太い線は見た目どおり触れたら消えてほしい）
  const reach = r + stroke.width / 2
  const r2 = reach * reach
  const X = cx * w
  const Y = cy * h

  if (n === 1) {
    const dx = pts[0] * w - X
    const dy = pts[1] * h - Y
    return dx * dx + dy * dy <= r2
  }
  for (let i = 0; i < n - 1; i++) {
    const ax = pts[i * 2] * w
    const ay = pts[i * 2 + 1] * h
    const bx = pts[(i + 1) * 2] * w
    const by = pts[(i + 1) * 2 + 1] * h
    if (distSqToSegment(X, Y, ax, ay, bx, by) <= r2) return true
  }
  return false
}

/**
 * 部分消し。消しゴム円に触れた線分は両端の点ごと落とし、
 * 残った連続区間を別々の断片にして返す。
 *
 * 交点で正確に切るのではなく線分単位で落とすので、点のまばらな線では
 * 穴が狙いより少し大きくなる（仕様の「制限」に明記済み）。
 *
 * @returns 触れていなければ null。触れていれば残った断片（消滅なら空配列）
 */
export function splitStroke(
  stroke: Stroke,
  cx: number,
  cy: number,
  r: number,
  w: number,
  h: number,
): number[][] | null {
  const pts = stroke.pts
  const n = pts.length >> 1
  if (n === 0) return null
  if (!hitsStroke(stroke, cx, cy, r, w, h)) return null

  const reach = r + stroke.width / 2
  const r2 = reach * reach
  const X = cx * w
  const Y = cy * h

  // 各点が「消える側」か。線分が円に触れたら、その両端を消す
  const dead = new Array<boolean>(n).fill(false)
  if (n === 1) {
    dead[0] = true
  } else {
    for (let i = 0; i < n - 1; i++) {
      const ax = pts[i * 2] * w
      const ay = pts[i * 2 + 1] * h
      const bx = pts[(i + 1) * 2] * w
      const by = pts[(i + 1) * 2 + 1] * h
      if (distSqToSegment(X, Y, ax, ay, bx, by) <= r2) {
        dead[i] = true
        dead[i + 1] = true
      }
    }
  }

  const parts: number[][] = []
  let run: number[] = []
  for (let i = 0; i < n; i++) {
    if (dead[i]) {
      // 1点だけ残った断片は捨てる（点として残ると消し残しに見える）
      if (run.length >= 4) parts.push(run)
      run = []
    } else {
      run.push(pts[i * 2], pts[i * 2 + 1])
    }
  }
  if (run.length >= 4) parts.push(run)
  return parts
}

/** 断片に新しい id を振る。元の id は消滅させる */
export function toParts(fragments: number[][]): StrokePart[] {
  return fragments.map((pts) => ({ id: crypto.randomUUID(), pts }))
}
